import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { ACTIVE_EQUIPMENT_TYPES, EQUIPMENT_TYPE_MAP } from '../data/equipmentTypes';

const MONO = "'IBM Plex Mono', monospace";

const filterOptions = (options, state) => {
  if (!state.inputValue) return options;
  const q = state.inputValue.toLowerCase();
  return options.filter(o =>
    o.label.toLowerCase().includes(q) ||
    o.value.toLowerCase().includes(q)
  );
};

export default function EquipmentTypePicker({ value, onChange, disabled }) {
  const resolvedValue = value
    ? (EQUIPMENT_TYPE_MAP[value] ?? value)
    : null;

  return (
    <Box sx={{ mb: '14px' }}>
      <Box
        component="label"
        sx={{
          display: 'block',
          fontFamily: MONO,
          fontSize: '10.5px',
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: '#7f93b8',
          mb: '6px',
        }}
      >
        Equipment Type
      </Box>
      <Autocomplete
        freeSolo
        disableClearable
        options={ACTIVE_EQUIPMENT_TYPES}
        value={resolvedValue}
        groupBy={(option) => option.category}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.label ?? option.value ?? '';
        }}
        isOptionEqualToValue={(option, val) =>
          option.value === (typeof val === 'object' && val !== null ? val.value : val)
        }
        filterOptions={filterOptions}
        disabled={disabled}
        onChange={(_, newVal) => {
          if (newVal === null) return;
          onChange(typeof newVal === 'object' ? newVal.value : newVal);
        }}
        onInputChange={(_, newInput, reason) => {
          if (reason === 'clear') onChange('node');
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.value}
            sx={{
              display: 'flex !important',
              alignItems: 'center !important',
              gap: '8px !important',
              fontSize: '13px !important',
              fontFamily: "'Lexend', sans-serif !important",
              color: '#dce6fa !important',
              py: '7px !important',
              px: '14px !important',
            }}
          >
            <Box sx={{ flex: 1 }}>{option.label}</Box>
            {option.cvDetectable && (
              <Box
                sx={{
                  fontSize: '10px',
                  fontFamily: MONO,
                  color: '#2dd4bf',
                  background: 'rgba(45,212,191,.15)',
                  px: '5px',
                  py: '2px',
                  borderRadius: '4px',
                  flexShrink: 0,
                  lineHeight: 1.6,
                }}
              >
                CV
              </Box>
            )}
          </Box>
        )}
        renderGroup={(params) => (
          <Box key={params.key}>
            <Box
              sx={{
                fontFamily: MONO,
                fontSize: '10.5px',
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: '#7f93b8',
                px: '14px',
                pt: '10px',
                pb: '3px',
                borderTop: '1px solid rgba(120,150,200,.1)',
              }}
            >
              {params.group}
            </Box>
            {params.children}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            onFocus={(e) => {
              params.onFocus?.(e);
              e.target.select();
            }}
            placeholder="Search or type custom type…"
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                background: '#0c1a32',
                fontSize: '13.5px',
                fontFamily: MONO,
                color: '#eaf0fb',
                borderRadius: '8px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(120,150,200,.26)',
              },
              '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(120,150,200,.5)',
              },
              '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2dd4bf',
                borderWidth: '1px',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#5f7398',
                opacity: 1,
              },
              '& .MuiSvgIcon-root': {
                color: '#5f7398',
              },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              background: '#0d1a33',
              border: '1px solid rgba(120,150,200,.24)',
              borderRadius: '10px',
              mt: '4px',
              boxShadow: '0 16px 40px rgba(0,0,0,.4)',
            },
          },
          listbox: {
            sx: { p: '4px 0', maxHeight: '280px' },
          },
        }}
      />
    </Box>
  );
}
